/**
 * Iframe `src` query param names shared by `src/sdk/transport/iframe.ts`
 * (`buildIframeSrc`, the builder) and `src/inputs/adapters/config.ts`
 * (the parser). `nonce` (`_`) is SDK-only cache-busting — `config.ts` never
 * reads it back — but it is still a wire-shape name both sides must agree
 * never collides with a real param.
 */
export const IFRAME_QUERY_PARAMS = {
  token: 'token',
  siteKey: 'site_key',
  group: 'group',
  nonce: '_',
  locale: 'locale',
  /** Per-field customization (Engram odd/sdk-options/tasks, item 2):
   * overrides the default input placeholder / accessible name. Cosmetic
   * only, never card data — same validate-in-`src/inputs`,
   * silent-fallback-when-invalid convention as the theme params above. */
  placeholder: 'placeholder',
  ariaLabel: 'aria_label',
  /** Per-field `style`-by-state customization (Engram odd/sdk-options/tasks,
   * item 5): one compact, URL-encoded JSON object
   * (`{ base?, focus?, invalid?, complete?, placeholder? }`). Cosmetic only,
   * never card data. `src/inputs/adapters/config.ts` parses it defensively
   * (2 KB cap, try/catch, plain-object check) and validates every property
   * against a closed CSS allowlist before applying anything. */
  style: 'style',
} as const;

/**
 * Merchant theme query params, cosmetic only (never card data). Maps the
 * SDK's public camelCase `IframeTheme` keys to `src/inputs`'s real
 * snake_case query param names. Validation regexes stay in
 * `src/inputs` (it owns the security boundary that parses these);
 * this map only fixes the shared vocabulary of names.
 */
export const THEME_QUERY_PARAMS = {
  accent: 'accent',
  accentHover: 'accent_hover',
  accentSoft: 'accent_soft',
  fontFamily: 'font_family',
  textColor: 'text_color',
  borderColor: 'border_color',
  borderRadius: 'border_radius',
  height: 'height',
  width: 'width',
  padding: 'padding',
} as const;

export type ThemeQueryParamKey = keyof typeof THEME_QUERY_PARAMS;
